import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { ScrollArea } from "../ui/scroll-area";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Server, Globe, Plus, Pencil, Trash2, Search, Download, Upload,
  RefreshCw, X, BarChart3, Link2, Wifi, WifiOff, Loader2,
  PlayCircle, Film, Tv, Eye, Check, AlertTriangle, Activity, Shield, FileDown, Calendar
} from "lucide-react";
import { StatsCategories } from "./StatsCategories";
import { COUNTRIES, getFlag, getCountryName } from "./constants";

export const ProvidersManagement = () => {
  const [providers, setProviders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hostsDialogOpen, setHostsDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    description: "", 
    enabled: true,
    max_display_per_day: 5,
    panel_url: "",
    m3u_url: ""
  });
  const { token } = useAuth();

  // Hosts management state
  const [hosts, setHosts] = useState([]);
  const [selectedHosts, setSelectedHosts] = useState(new Set());
  const [editingHostId, setEditingHostId] = useState(null);
  const [editingNote, setEditingNote] = useState("");
  const [addHostDialogOpen, setAddHostDialogOpen] = useState(false);
  const [validatingHosts, setValidatingHosts] = useState(false);
  const [scanningHosts, setScanningHosts] = useState(false);
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);

  // Analysis state
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisProvider, setAnalysisProvider] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsProvider, setStatsProvider] = useState(null);
  const [categorySearchFilter, setCategorySearchFilter] = useState("");
  const [newHostData, setNewHostData] = useState({ host: "", note: "" });
  const [bulkHostsText, setBulkHostsText] = useState("");
  const [bulkErrors, setBulkErrors] = useState([]);
  const [loadingHosts, setLoadingHosts] = useState(false);

  // CSV Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSkipDuplicates, setImportSkipDuplicates] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);

  // IP Lookup state
  const [hostIPs, setHostIPs] = useState({});
  const [loadingIPs, setLoadingIPs] = useState(false);

  // Filters state
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "inactive"

  // Smart bulk insert state
  const [smartBulkDialogOpen, setSmartBulkDialogOpen] = useState(false);
  const [smartBulkHosts, setSmartBulkHosts] = useState("");
  const [smartBulkLoading, setSmartBulkLoading] = useState(false);
  const [smartBulkResult, setSmartBulkResult] = useState(null);

  // Analyze All state
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [analyzeAllProgress, setAnalyzeAllProgress] = useState({ current: 0, total: 0 });
  const [analyzeAllResults, setAnalyzeAllResults] = useState({ success: 0, failed: 0, proxy: 0 });
  const analyzeAllAbortRef = useRef(false);

  // Status filter for dashboard
  const [dashboardStatusFilter, setDashboardStatusFilter] = useState("all"); // "all", "panel_online", "panel_offline", "m3u_valid", "m3u_invalid", "proxy", "not_tested"

  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du chargement des fournisseurs");
    }
  }, [token]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Filtered providers - search by name, description, host, IP, or categories
  const filteredProviders = providers.filter(provider => {
    // Search filter - check name, description, hosts, IPs and categories
    const searchLower = searchFilter.toLowerCase();
    
    let matchesSearch = searchFilter === "" || 
      provider.name.toLowerCase().includes(searchLower) ||
      (provider.description && provider.description.toLowerCase().includes(searchLower));
    
    // Also search in hosts and IPs
    if (!matchesSearch && provider.hosts && Array.isArray(provider.hosts)) {
      matchesSearch = provider.hosts.some(host => {
        if (typeof host === 'string') {
          return host.toLowerCase().includes(searchLower);
        }
        if (typeof host === 'object') {
          // Check host name
          if (host.host && host.host.toLowerCase().includes(searchLower)) {
            return true;
          }
          // Check IP
          if (host.ip_info && host.ip_info.ip && host.ip_info.ip.includes(searchLower)) {
            return true;
          }
          // Check note
          if (host.note && host.note.toLowerCase().includes(searchLower)) {
            return true;
          }
        }
        return false;
      });
    }
    
    // Search in analysis categories (name and count)
    if (!matchesSearch && provider.analysis?.categories) {
      const cats = provider.analysis.categories;
      const allCategories = [...(cats.live || []), ...(cats.vod || []), ...(cats.series || [])];
      matchesSearch = allCategories.some(cat => {
        // Search by category name
        if (cat.name && cat.name.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search by "name count" pattern (e.g., "france tnt 52")
        if (cat.name && cat.count) {
          const nameWithCount = `${cat.name} ${cat.count}`.toLowerCase();
          if (nameWithCount.includes(searchLower)) {
            return true;
          }
        }
        return false;
      });
    }
    
    // Status filter
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && provider.enabled) ||
      (statusFilter === "inactive" && !provider.enabled);
    
    return matchesSearch && matchesStatus;
  });

  const fetchHosts = useCallback(async (providerId) => {
    setLoadingHosts(true);
    try {
      const response = await axios.get(`${API}/admin/providers/${providerId}/hosts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHosts(response.data);
      
      // Load saved IPs from host data
      const savedIPs = {};
      response.data.forEach(host => {
        if (host.ip_info) {
          savedIPs[host.host] = {
            ip: host.ip_info.ip,
            country: host.ip_info.country,
            country_code: host.ip_info.country_code,
            isp: host.ip_info.isp,
            status: 'success'
          };
        }
      });
      setHostIPs(savedIPs);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du chargement des hosts");
    } finally {
      setLoadingHosts(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await axios.put(`${API}/admin/providers/${editingProvider.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fournisseur mis à jour");
      } else {
        await axios.post(`${API}/admin/providers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fournisseur créé");
      }
      setDialogOpen(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", enabled: true, max_display_per_day: 5, panel_url: "", m3u_url: "" });
    setEditingProvider(null);
  };

  const toggleProvider = async (providerId) => {
    try {
      await axios.post(`${API}/admin/providers/${providerId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const deleteProvider = async (providerId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fournisseur ?")) return;
    try {
      await axios.delete(`${API}/admin/providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProviders();
      toast.success("Fournisseur supprimé");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const openEditDialog = (provider) => {
    setEditingProvider(provider);
    setFormData({ 
      name: provider.name, 
      description: provider.description || "", 
      enabled: provider.enabled ?? true,
      max_display_per_day: provider.max_display_per_day ?? 5,
      panel_url: provider.panel_url || "",
      m3u_url: provider.m3u_url || ""
    });
    setDialogOpen(true);
  };

  const openHostsDialog = async (provider) => {
    setSelectedProvider(provider);
    setSelectedHosts(new Set());
    setEditingHostId(null);
    await fetchHosts(provider.id);
    setHostsDialogOpen(true);
  };

  // Host management functions
  const handleAddHost = async () => {
    try {
      await axios.post(`${API}/admin/providers/${selectedProvider.id}/hosts`, newHostData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Host ajouté");
      setNewHostData({ host: "", note: "" });
      setAddHostDialogOpen(false);
      fetchHosts(selectedProvider.id);
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleBulkAdd = async () => {
    setBulkErrors([]);
    const hostsArray = bulkHostsText
      .split(/[\n,]/)
      .map(h => h.trim())
      .filter(h => h.length > 0);
    
    if (hostsArray.length === 0) {
      toast.error("Aucun host à ajouter");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/providers/${selectedProvider.id}/hosts/bulk`,
        { hosts: hostsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success.length > 0) {
        toast.success(`${response.data.success.length} host(s) ajouté(s)`);
      }
      
      if (response.data.errors.length > 0) {
        setBulkErrors(response.data.errors);
      } else {
        setBulkHostsText("");
        setBulkAddDialogOpen(false);
      }
      
      fetchHosts(selectedProvider.id);
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout en masse");
    }
  };

  const handleUpdateNote = async (hostId) => {
    try {
      await axios.put(
        `${API}/admin/providers/${selectedProvider.id}/hosts/${hostId}`,
        { note: editingNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Note mise à jour");
      setEditingHostId(null);
      fetchHosts(selectedProvider.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleDeleteHost = async (hostId) => {
    if (!window.confirm("Supprimer ce host ?")) return;
    try {
      await axios.delete(`${API}/admin/providers/${selectedProvider.id}/hosts/${hostId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Host supprimé");
      fetchHosts(selectedProvider.id);
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedHosts.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedHosts.size} host(s) ?`)) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/providers/${selectedProvider.id}/hosts/bulk-delete`,
        { host_ids: Array.from(selectedHosts) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.deleted.length > 0) {
        toast.success(`${response.data.deleted.length} host(s) supprimé(s)`);
      }
      
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} erreur(s) de suppression`);
      }
      
      setSelectedHosts(new Set());
      fetchHosts(selectedProvider.id);
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const toggleSelectAll = () => {
    if (selectedHosts.size === hosts.length) {
      setSelectedHosts(new Set());
    } else {
      setSelectedHosts(new Set(hosts.map(h => h.id)));
    }
  };

  const toggleSelectHost = (hostId) => {
    const newSelected = new Set(selectedHosts);
    if (newSelected.has(hostId)) {
      newSelected.delete(hostId);
    } else {
      newSelected.add(hostId);
    }
    setSelectedHosts(newSelected);
  };

  const startEditNote = (host) => {
    setEditingHostId(host.id);
    setEditingNote(host.note || "");
  };

  const getHostValue = (host) => {
    if (typeof host === 'string') return host;
    return host?.host || '';
  };

  // IP Lookup function - saves to database
  const handleLookupIPs = async () => {
    if (hosts.length === 0 || !selectedProvider) return;
    
    setLoadingIPs(true);
    try {
      const response = await axios.post(
        `${API}/admin/providers/${selectedProvider.id}/resolve-all-ips`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const ipMap = {};
      response.data.results.forEach(result => {
        ipMap[result.host] = result;
      });
      setHostIPs(prev => ({ ...prev, ...ipMap }));
      
      const successCount = response.data.results.filter(r => r.status === 'success').length;
      toast.success(`${successCount}/${hosts.length} IPs résolues et sauvegardées`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la résolution des IPs");
    } finally {
      setLoadingIPs(false);
    }
  };

  // Single IP Lookup - saves to database
  const handleLookupSingleIP = async (hostData) => {
    if (!selectedProvider) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/providers/${selectedProvider.id}/hosts/${hostData.id}/resolve-ip`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHostIPs(prev => ({ ...prev, [hostData.host]: response.data }));
      if (response.data.status === 'success') {
        toast.success(`IP résolue: ${response.data.ip}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la résolution de l'IP");
    }
  };

  // Trigger DNS scan of all hosts of the current provider
  const handleScanHosts = async () => {
    if (!selectedProvider) return;
    setScanningHosts(true);
    try {
      const response = await axios.post(
        `${API}/admin/providers/${selectedProvider.id}/scan-hosts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { total, ok, failed, duration_s } = response.data;
      toast.success(`Scan termine: ${ok}/${total} OK, ${failed} KO en ${duration_s}s`);
      // Reload hosts so the new dns_check appears on each row
      if (selectedProvider) {
        fetchHosts(selectedProvider.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du scan des hosts");
    } finally {
      setScanningHosts(false);
    }
  };

  // Validate all hosts with M3U API
  const handleValidateAllHosts = async () => {
    if (!selectedProvider || !selectedProvider.m3u_url) {
      toast.error("Ce fournisseur n'a pas d'URL M3U configurée");
      return;
    }
    
    setValidatingHosts(true);
    try {
      const response = await axios.post(
        `${API}/admin/providers/${selectedProvider.id}/validate-all-hosts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update hosts with validation results including validation_error
      const updatedHosts = hosts.map(host => {
        const result = response.data.results.find(r => r.host === host.host);
        if (result) {
          return { 
            ...host, 
            is_valid: result.is_valid, 
            last_check: response.data.check_time || new Date().toISOString(), 
            response_time: result.response_time,
            validation_error: result.error || null
          };
        }
        return host;
      });
      setHosts(updatedHosts);
      
      toast.success(`✓ Validation rapide terminée: ${response.data.valid}/${response.data.total} hosts OK`);
      fetchProviders(); // Refresh providers list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidatingHosts(false);
    }
  };

  // Analyze ALL providers function - NOW USES QUICK CHECK (FAST!)
  const handleAnalyzeAllProviders = async () => {
    if (providers.length === 0) return;
    
    analyzeAllAbortRef.current = false;
    setAnalyzingAll(true);
    setAnalyzeAllProgress({ current: 0, total: providers.length });
    setAnalyzeAllResults({ success: 0, failed: 0, proxy: 0 });
    
    try {
      // Use the new quick-check-all endpoint for parallel checking
      toast.info("Vérification rapide de tous les fournisseurs...");
      
      const response = await axios.post(
        `${API}/admin/providers/quick-check-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { stats, results } = response.data;
      
      setAnalyzeAllResults({
        success: stats.m3u_valid,
        failed: stats.m3u_invalid,
        proxy: stats.proxy_used
      });
      setAnalyzeAllProgress({ current: stats.total, total: stats.total });
      
      fetchProviders(); // Refresh all providers data
      toast.success(`✅ Terminé: ${stats.m3u_valid} OK, ${stats.m3u_invalid} échoués, ${stats.proxy_used} via proxy`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la vérification");
      console.error("Provider operation failed");
    } finally {
      setAnalyzingAll(false);
    }
  };

  const handleStopAnalyzeAll = () => {
    analyzeAllAbortRef.current = true;
    toast.info("L'analyse en parallèle ne peut pas être interrompue");
  };

  // Filter providers for status dashboard
  const getFilteredDashboardProviders = () => {
    return providers.filter(provider => {
      // Use quick_check if available, otherwise fall back to analysis
      const checkData = provider.quick_check || provider.analysis || {};
      
      switch (dashboardStatusFilter) {
        case "panel_online":
          return checkData.panel_status === "online";
        case "panel_offline":
          return checkData.panel_status === "offline";
        case "m3u_valid":
          return checkData.m3u_status === "valid";
        case "m3u_invalid":
          return checkData.m3u_status === "invalid";
        case "proxy":
          return checkData.proxy_used || checkData.m3u_status === "proxy_required";
        case "not_tested":
          return !provider.quick_check && !provider.analysis;
        default:
          return true;
      }
    });
  };

  // Bulk resolve all providers (with 5 second delay)
  const [bulkResolvingAll, setBulkResolvingAll] = useState(false);
  const [bulkResolveProgress, setBulkResolveProgress] = useState({ current: 0, total: 0, currentProvider: '' });

  const handleBulkResolveAllProviders = async () => {
    if (providers.length === 0) return;
    
    setBulkResolvingAll(true);
    setBulkResolveProgress({ current: 0, total: providers.length, currentProvider: '' });
    
    for (let i = 0; i < providers.length; i++) {
      const provider = providers[i];
      setBulkResolveProgress({ current: i + 1, total: providers.length, currentProvider: provider.name });
      
      try {
        await axios.post(
          `${API}/admin/providers/${provider.id}/resolve-all-ips`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error(`Erreur pour ${provider.name}`);
      }
      
      // Wait 5 seconds before next provider (except for last one)
      if (i < providers.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }
    
    setBulkResolvingAll(false);
    setBulkResolveProgress({ current: 0, total: 0, currentProvider: '' });
    toast.success(`Résolution terminée pour ${providers.length} fournisseurs`);
    fetchProviders(); // Refresh to show updated data
  };

  // Analyze Provider function
  // Helper function to fetch data via proxy (from user's browser)
  const fetchViaProxy = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors'
      });
      if (response.ok) {
        const text = await response.text();
        if (text && text.length > 0) {
          return JSON.parse(text);
        }
      }
      return null;
    } catch (error) {
      console.error('Proxy fetch error for', url);
      return null;
    }
  };

  const handleAnalyzeProvider = async (provider) => {
    setAnalysisProvider(provider);
    setAnalysisResult(null);
    setAnalysisDialogOpen(true);
    setAnalyzing(true);

    try {
      const response = await axios.post(
        `${API}/admin/providers/${provider.id}/analyze`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if proxy mode is required
      if (response.data.proxy_required && response.data.proxy_urls) {
        // Check if URLs are HTTP (will cause Mixed Content error on HTTPS site)
        const authUrl = response.data.proxy_urls.auth || '';
        const isHttpOnly = authUrl.startsWith('http://') && window.location.protocol === 'https:';
        
        if (isHttpOnly) {
          // Cannot fetch HTTP from HTTPS - show manual test option
          setAnalysisResult({
            ...response.data,
            error: "Ce fournisseur bloque les requêtes de notre serveur (blocage IP datacenter). De plus, le serveur utilise HTTP ce qui empêche les requêtes directes depuis votre navigateur (sécurité Mixed Content).",
            manual_test_urls: response.data.proxy_urls
          });
          toast.warning("Blocage IP détecté - Test manuel requis");
        } else {
          // Try browser proxy for HTTPS URLs
          toast.info("Mode proxy activé - Récupération des données via votre navigateur...");
          
          const urls = response.data.proxy_urls;
          const proxyData = {
            provider_id: provider.id,
            auth_data: null,
            live_categories: null,
            live_streams: null,
            vod_categories: null,
            vod_streams: null,
            series_categories: null,
            series: null,
            error: null
          };
          
          try {
            proxyData.auth_data = await fetchViaProxy(urls.auth);
            
            if (proxyData.auth_data && proxyData.auth_data.user_info) {
              const [liveCats, liveStreams, vodCats, vodStreams, seriesCats, series] = await Promise.all([
                fetchViaProxy(urls.live_categories),
                fetchViaProxy(urls.live_streams),
                fetchViaProxy(urls.vod_categories),
                fetchViaProxy(urls.vod_streams),
                fetchViaProxy(urls.series_categories),
                fetchViaProxy(urls.series)
              ]);
              
              proxyData.live_categories = liveCats;
              proxyData.live_streams = liveStreams;
              proxyData.vod_categories = vodCats;
              proxyData.vod_streams = vodStreams;
              proxyData.series_categories = seriesCats;
              proxyData.series = series;
            } else {
              proxyData.error = "Authentification échouée ou compte expiré";
            }
          } catch (proxyError) {
            proxyData.error = "Erreur lors de la récupération des données: " + proxyError.message;
          }
          
          const proxyResponse = await axios.post(
            `${API}/admin/providers/${provider.id}/analyze-proxy`,
            proxyData,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setAnalysisResult(proxyResponse.data);
          toast.success("Analyse via proxy terminée");
        }
      } else {
        setAnalysisResult(response.data);
      }
      
      fetchProviders(); // Refresh to get updated stats
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
      setAnalysisResult({ error: error.response?.data?.detail || "Erreur inconnue" });
    } finally {
      setAnalyzing(false);
    }
  };

  // Smart Bulk Insert function
  const handleSmartBulkInsert = async () => {
    const hostsArray = smartBulkHosts
      .split('\n')
      .map(h => h.trim())
      .filter(h => h.length > 0);
    
    if (hostsArray.length === 0) {
      toast.error("Veuillez entrer au moins un host");
      return;
    }

    setSmartBulkLoading(true);
    setSmartBulkResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/providers/smart-bulk-insert`,
        { hosts: hostsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSmartBulkResult(response.data);
      
      if (response.data.inserted > 0) {
        toast.success(`${response.data.inserted} host(s) inséré(s) avec succès`);
        fetchProviders(); // Refresh providers
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'insertion en masse");
    } finally {
      setSmartBulkLoading(false);
    }
  };

  const resetSmartBulkDialog = () => {
    setSmartBulkHosts("");
    setSmartBulkResult(null);
  };

  // CSV Export function
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/providers/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fournisseurs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export CSV réussi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'export CSV");
    }
  };

  // CSV Template download
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/providers/template/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_fournisseurs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template téléchargé");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement du template");
    }
  };

  // CSV Import function
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error("Veuillez sélectionner un fichier CSV");
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(
        `${API}/admin/providers/import/csv?skip_duplicates=${importSkipDuplicates}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} fournisseur(s) importé(s) avec succès`);
        fetchProviders();
      } else if (response.data.errors > 0) {
        toast.error("Aucun fournisseur importé - vérifiez les erreurs");
      } else if (response.data.skipped > 0) {
        toast.info("Tous les fournisseurs existaient déjà");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import CSV");
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setImportFile(null);
    setImportSkipDuplicates(true);
    setImportResult(null);
    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="providers-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Fournisseurs</h1>
        <div className="flex items-center gap-2">
          {/* Smart Bulk Insert Button */}
          <Dialog open={smartBulkDialogOpen} onOpenChange={(open) => {
            setSmartBulkDialogOpen(open);
            if (!open) resetSmartBulkDialog();
          }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                data-testid="smart-bulk-insert"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajout intelligent
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="smart-bulk-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-green-400" />
                  Ajout intelligent de hosts
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 flex-1 overflow-auto">
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm text-green-400 font-medium mb-1">Comment ça marche ?</p>
                  <p className="text-xs text-muted-foreground">
                    1. Entrez les hosts ou URLs M3U (un par ligne)<br/>
                    2. Le système extrait le host et résout l'IP<br/>
                    3. Compare avec les IPs existantes des fournisseurs<br/>
                    4. Insère automatiquement dans le bon fournisseur
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Hosts ou URLs M3U à ajouter (un par ligne)</Label>
                  <Textarea
                    value={smartBulkHosts}
                    onChange={(e) => setSmartBulkHosts(e.target.value)}
                    placeholder="exemple1.host.com&#10;http://max.host.com:2052/get.php?username=xxx&password=xxx&type=m3u_plus&#10;stream.provider.tv"
                    className="min-h-[150px] font-mono text-sm"
                    data-testid="smart-bulk-textarea"
                  />
                  <p className="text-xs text-muted-foreground">
                    {smartBulkHosts.split('\n').filter(h => h.trim()).length} entrée(s) à traiter • Accepte hosts simples ou URLs M3U complètes
                  </p>
                </div>

                {/* Results */}
                {smartBulkResult && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm p-3 bg-muted/50 rounded-lg">
                      <span>Total: <strong>{smartBulkResult.total}</strong></span>
                      <span className="text-green-400">Insérés: <strong>{smartBulkResult.inserted}</strong></span>
                      <span className="text-yellow-400">Non trouvés: <strong>{smartBulkResult.not_found}</strong></span>
                      <span className="text-orange-400">Doublons: <strong>{smartBulkResult.duplicates}</strong></span>
                      <span className="text-red-400">Erreurs: <strong>{smartBulkResult.errors}</strong></span>
                    </div>

                    <ScrollArea className="h-[200px] border rounded-md">
                      <div className="p-2 space-y-1">
                        {smartBulkResult.results.map((result, idx) => (
                          <div 
                            key={idx}
                            className={`text-xs p-2 rounded flex items-center justify-between ${
                              result.status === 'inserted' ? 'bg-green-500/10 text-green-400' :
                              result.status === 'not_found' ? 'bg-yellow-500/10 text-yellow-400' :
                              result.status === 'duplicate' ? 'bg-orange-500/10 text-orange-400' :
                              'bg-red-500/10 text-red-400'
                            }`}
                          >
                            <span className="font-mono">{result.host}</span>
                            <span className="flex items-center gap-2">
                              {result.ip && <span className="text-muted-foreground">{result.ip}</span>}
                              <span className="font-medium">
                                {result.status === 'inserted' ? `→ ${result.provider_name}` : result.message}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSmartBulkDialogOpen(false);
                    resetSmartBulkDialog();
                  }}
                >
                  Fermer
                </Button>
                <Button 
                  onClick={handleSmartBulkInsert}
                  disabled={smartBulkLoading || !smartBulkHosts.trim()}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="smart-bulk-submit"
                >
                  {smartBulkLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Lancer l'insertion
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bulk Resolve All IPs Button */}
          <Button 
            variant="outline" 
            onClick={handleBulkResolveAllProviders}
            disabled={bulkResolvingAll || providers.length === 0}
            data-testid="bulk-resolve-all-ips"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          >
            {bulkResolvingAll ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                {bulkResolveProgress.current}/{bulkResolveProgress.total} - {bulkResolveProgress.currentProvider}
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 mr-2" />
                Résoudre toutes les IPs
              </>
            )}
          </Button>

          {/* Export CSV Button */}
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
            data-testid="export-providers-csv"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>

          {/* Import CSV Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) resetImportDialog();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-providers-csv">
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="import-providers-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Importer des fournisseurs
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Template download */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">Template CSV</p>
                    <p className="text-sm text-muted-foreground">
                      Téléchargez le modèle pour le format attendu
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                    <FileDown className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                </div>

                {/* File input */}
                <div className="space-y-2">
                  <Label>Fichier CSV *</Label>
                  <Input
                    ref={importFileRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    data-testid="import-file-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Format: nom;description;actif;limite_affichage;hosts
                  </p>
                </div>

                {/* Options */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={importSkipDuplicates}
                    onCheckedChange={setImportSkipDuplicates}
                    id="skip-duplicates"
                  />
                  <Label htmlFor="skip-duplicates" className="cursor-pointer">
                    Ignorer les fournisseurs existants (par nom)
                  </Label>
                </div>

                {/* Format explanation */}
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm font-medium text-blue-400 mb-2">Format des hosts</p>
                  <p className="text-xs text-muted-foreground">
                    Les hosts sont séparés par des virgules. Pour ajouter une note, utilisez le format: <code className="bg-muted px-1 rounded">host|note</code>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Exemple: <code className="bg-muted px-1 rounded">stream.tv|Principal,cdn.tv|Backup,api.tv</code>
                  </p>
                </div>

                {/* Import result */}
                {importResult && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                    <div className="flex items-center gap-4 text-sm">
                      <span>Total: <strong>{importResult.total}</strong></span>
                      <span className="text-green-500">Succès: <strong>{importResult.success}</strong></span>
                      <span className="text-red-500">Erreurs: <strong>{importResult.errors}</strong></span>
                      <span className="text-yellow-500">Ignorés: <strong>{importResult.skipped}</strong></span>
                    </div>
                    
                    {importResult.details && importResult.details.length > 0 && (
                      <ScrollArea className="h-40 border rounded-md p-2">
                        <div className="space-y-1">
                          {importResult.details.map((detail, idx) => (
                            <div 
                              key={idx} 
                              className={`text-xs p-2 rounded ${
                                detail.status === 'success' ? 'bg-green-500/10 text-green-400' :
                                detail.status === 'skipped' ? 'bg-yellow-500/10 text-yellow-400' :
                                'bg-red-500/10 text-red-400'
                              }`}
                            >
                              <span className="font-mono">Ligne {detail.line}:</span> {detail.message}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setImportDialogOpen(false);
                    resetImportDialog();
                  }}
                >
                  Fermer
                </Button>
                <Button 
                  onClick={handleImportCSV}
                  disabled={!importFile || importing}
                  data-testid="import-submit-btn"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Provider Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground" data-testid="add-provider-button">
                <Plus className="h-4 w-4 mr-2" />
                Nouveau Fournisseur
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="provider-form-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  {editingProvider ? "Modifier le fournisseur" : "Nouveau fournisseur"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: IPTV Provider"
                    required
                    data-testid="provider-form-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description du fournisseur"
                    data-testid="provider-form-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Limite d'affichage quotidienne</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.max_display_per_day}
                    onChange={(e) => setFormData({ ...formData, max_display_per_day: parseInt(e.target.value) || 5 })}
                    placeholder="5"
                    data-testid="provider-form-max-display"
                  />
                  <p className="text-xs text-muted-foreground">
                    Nombre maximum de hosts qu'un utilisateur peut afficher par jour
                  </p>
                </div>
                
                {/* Panel & M3U URLs for analysis */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    URLs pour analyse
                  </p>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Link2 className="h-3 w-3" />
                        URL du Panel Admin
                      </Label>
                      <Input
                        value={formData.panel_url}
                        onChange={(e) => setFormData({ ...formData, panel_url: e.target.value })}
                        placeholder="https://panel.provider.com"
                        data-testid="provider-form-panel-url"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Tv className="h-3 w-3" />
                        URL M3U (Xtream Codes)
                      </Label>
                      <Input
                        value={formData.m3u_url}
                        onChange={(e) => setFormData({ ...formData, m3u_url: e.target.value })}
                        placeholder="http://host:port/get.php?username=xxx&password=xxx&type=m3u_plus"
                        data-testid="provider-form-m3u-url"
                      />
                      <p className="text-xs text-muted-foreground">
                        Renseignez ces URLs pour activer la fonction d'analyse
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                    data-testid="provider-form-enabled"
                  />
                  <Label>Fournisseur actif</Label>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="provider-form-submit">
                    {editingProvider ? "Mettre à jour" : "Créer"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters Section */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
        {/* Search Input */}
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, host ou IP..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-9"
              data-testid="search-providers"
            />
          </div>
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="inactive">Inactifs</SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {(searchFilter || statusFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchFilter("");
              setStatusFilter("all");
            }}
            data-testid="clear-filters"
          >
            <X className="h-4 w-4 mr-1" />
            Réinitialiser
          </Button>
        )}

        {/* Results count */}
        <div className="text-sm text-muted-foreground ml-auto">
          {filteredProviders.length} / {providers.length} fournisseur(s)
        </div>
      </div>

      {/* Status Dashboard - Quick Overview */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            État des Fournisseurs
          </h2>
          <div className="flex items-center gap-3">
            {/* Analyze All Button */}
            {!analyzingAll ? (
              <Button
                size="sm"
                onClick={handleAnalyzeAllProviders}
                className="bg-primary"
                data-testid="analyze-all-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Analyser tout
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm font-medium">
                    {analyzeAllProgress.current}/{analyzeAllProgress.total}
                  </span>
                  <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                    {analyzeAllProgress.currentProvider}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleStopAnalyzeAll}
                >
                  <X className="h-4 w-4 mr-1" />
                  Stop
                </Button>
              </div>
            )}
            
            {/* Legend */}
            <div className="flex items-center gap-3 text-xs border-l border-border pl-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-muted-foreground">OK</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span className="text-muted-foreground">OFF</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                <span className="text-muted-foreground">Proxy</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span className="text-muted-foreground">?</span>
              </div>
            </div>
          </div>
        </div>

        {/* Analyze All Progress Bar */}
        {analyzingAll && (
          <div className="mb-3 p-3 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Analyse en cours...</span>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-green-400">✓ {analyzeAllResults.success} OK</span>
                <span className="text-red-400">✗ {analyzeAllResults.failed} échoués</span>
                <span className="text-blue-400">🛡 {analyzeAllResults.proxy} proxy</span>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(analyzeAllProgress.current / analyzeAllProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Status Filters */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Filtrer:</span>
          {[
            { id: "all", label: "Tous", icon: null, count: providers.length },
            { id: "panel_online", label: "Panel OK", icon: <Wifi className="h-3 w-3" />, count: providers.filter(p => (p.quick_check || p.analysis)?.panel_status === 'online').length, color: "text-green-400" },
            { id: "panel_offline", label: "Panel OFF", icon: <WifiOff className="h-3 w-3" />, count: providers.filter(p => (p.quick_check || p.analysis)?.panel_status === 'offline').length, color: "text-red-400" },
            { id: "m3u_valid", label: "M3U OK", icon: <Check className="h-3 w-3" />, count: providers.filter(p => (p.quick_check || p.analysis)?.m3u_status === 'valid').length, color: "text-green-400" },
            { id: "m3u_invalid", label: "M3U OFF", icon: <X className="h-3 w-3" />, count: providers.filter(p => (p.quick_check || p.analysis)?.m3u_status === 'invalid').length, color: "text-red-400" },
            { id: "proxy", label: "Via Proxy", icon: <Shield className="h-3 w-3" />, count: providers.filter(p => (p.quick_check || p.analysis)?.proxy_used || (p.quick_check || p.analysis)?.m3u_status === 'proxy_required').length, color: "text-blue-400" },
            { id: "not_tested", label: "Non testé", icon: <AlertTriangle className="h-3 w-3" />, count: providers.filter(p => !p.quick_check && !p.analysis).length, color: "text-yellow-400" },
          ].map((filter) => (
            <Button
              key={filter.id}
              size="sm"
              variant={dashboardStatusFilter === filter.id ? "default" : "outline"}
              className={`h-7 text-xs ${dashboardStatusFilter !== filter.id && filter.color ? filter.color : ''}`}
              onClick={() => setDashboardStatusFilter(filter.id)}
            >
              {filter.icon && <span className="mr-1">{filter.icon}</span>}
              {filter.label}
              <span className="ml-1 opacity-70">({filter.count})</span>
            </Button>
          ))}
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
          {getFilteredDashboardProviders().slice(0, 24).map((provider) => {
            // Use quick_check if available, otherwise fall back to analysis
            const checkData = provider.quick_check || provider.analysis || {};
            const panelStatus = checkData.panel_status;
            const m3uStatus = checkData.m3u_status;
            const proxyUsed = checkData.proxy_used;
            const lastCheck = checkData.last_check || checkData.last_analysis;
            
            const getPanelColor = () => {
              if (!panelStatus || panelStatus === 'unknown') return 'bg-yellow-500';
              return panelStatus === 'online' ? 'bg-green-500' : 'bg-red-500';
            };
            
            const getM3uColor = () => {
              if (!m3uStatus || m3uStatus === 'unknown') return 'bg-yellow-500';
              if (m3uStatus === 'valid') return 'bg-green-500';
              if (m3uStatus === 'proxy_required') return 'bg-orange-500';
              return 'bg-red-500';
            };
            
            return (
              <div
                key={provider.id}
                className="relative bg-card border border-border rounded-lg p-2 hover:border-primary/50 transition-all cursor-pointer group"
                onClick={() => handleAnalyzeProvider(provider)}
                title={`Cliquer pour analyser ${provider.name}`}
              >
                {/* Provider Name */}
                <p className="text-xs font-medium truncate mb-2 pr-1" title={provider.name}>
                  {provider.name.length > 12 ? provider.name.substring(0, 12) + '...' : provider.name}
                </p>
                
                {/* Status Indicators */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {/* Panel Status */}
                    <div className="flex flex-col items-center" title={`Panel: ${panelStatus || 'Non testé'}`}>
                      <div className={`w-3 h-3 rounded-full ${getPanelColor()} ${panelStatus === 'online' ? 'animate-pulse' : ''}`}></div>
                      <span className="text-[8px] text-muted-foreground mt-0.5">P</span>
                    </div>
                    
                    {/* M3U Status */}
                    <div className="flex flex-col items-center" title={`M3U: ${m3uStatus || 'Non testé'}`}>
                      <div className={`w-3 h-3 rounded-full ${getM3uColor()}`}></div>
                      <span className="text-[8px] text-muted-foreground mt-0.5">M</span>
                    </div>
                  </div>
                  
                  {/* Stats mini */}
                  {provider.analysis?.live_count > 0 && (
                    <span className="text-[9px] text-muted-foreground font-mono">
                      {(provider.analysis.live_count / 1000).toFixed(1)}k
                    </span>
                  )}
                </div>
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-primary/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-primary" />
                </div>
                
                {/* Proxy indicator */}
                {proxyUsed && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center" title={`Via proxy: ${proxyUsed}`}>
                    <Shield className="h-2 w-2 text-white" />
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Show more indicator */}
          {getFilteredDashboardProviders().length > 24 && (
            <div className="bg-muted/30 border border-dashed border-border rounded-lg p-2 flex items-center justify-center">
              <span className="text-xs text-muted-foreground">+{getFilteredDashboardProviders().length - 24} autres</span>
            </div>
          )}
        </div>
        
        {/* Summary Stats */}
        <div className="flex items-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 rounded-full">
            <Wifi className="h-3 w-3 text-green-400" />
            <span className="text-green-400 font-medium">
              {providers.filter(p => (p.quick_check || p.analysis)?.panel_status === 'online').length} panels OK
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 rounded-full">
            <Check className="h-3 w-3 text-green-400" />
            <span className="text-green-400 font-medium">
              {providers.filter(p => (p.quick_check || p.analysis)?.m3u_status === 'valid').length} M3U OK
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-red-500/10 rounded-full">
            <WifiOff className="h-3 w-3 text-red-400" />
            <span className="text-red-400 font-medium">
              {providers.filter(p => (p.quick_check || p.analysis)?.panel_status === 'offline').length} hors ligne
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 rounded-full">
            <Shield className="h-3 w-3 text-blue-400" />
            <span className="text-blue-400 font-medium">
              {providers.filter(p => (p.quick_check || p.analysis)?.proxy_used).length} via proxy
            </span>
          </div>
          {dashboardStatusFilter !== "all" && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs text-muted-foreground"
              onClick={() => setDashboardStatusFilter("all")}
            >
              <X className="h-3 w-3 mr-1" />
              Réinitialiser filtre
            </Button>
          )}
        </div>
      </div>

      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProviders.map((provider) => (
          <Card 
            key={provider.id} 
            className={`bg-card border-border transition-all ${
              provider.enabled ? "hover:border-primary/30" : "opacity-50"
            }`}
            data-testid={`provider-card-${provider.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-sm ${provider.enabled ? "bg-primary/10" : "bg-muted"}`}>
                    <Server className={`h-5 w-5 ${provider.enabled ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{provider.name}</h3>
                    <p className="text-xs text-muted-foreground">{provider.description}</p>
                    <p className="text-xs text-accent font-mono">Max: {provider.max_display_per_day || 5}/jour</p>
                  </div>
                </div>
                <Switch
                  checked={provider.enabled}
                  onCheckedChange={() => toggleProvider(provider.id)}
                  data-testid={`toggle-provider-${provider.id}`}
                />
              </div>
              
              {/* Hosts preview */}
              <div className="mb-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Hosts ({provider.hosts?.length || 0})
                </p>
                <div className="space-y-1">
                  {(provider.hosts || []).slice(0, 3).map((host, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <Globe className="h-3 w-3 text-accent" />
                      <span className="font-mono text-muted-foreground">{getHostValue(host)}</span>
                    </div>
                  ))}
                  {(provider.hosts || []).length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{provider.hosts.length - 3} autres...
                    </p>
                  )}
                </div>
              </div>

              {/* Analysis Summary - Show if analysis exists */}
              {provider.analysis && (
                <div className="mb-3 p-2 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      {provider.analysis.m3u_status !== 'valid' && provider.analysis.last_successful_analysis ? 'Dernière vérification' : 'Dernière analyse'}
                    </p>
                    <span className="text-[10px] text-muted-foreground">
                      {(provider.analysis.last_check || provider.analysis.last_analysis) ? new Date(provider.analysis.last_check || provider.analysis.last_analysis).toLocaleString('fr-FR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : ''}
                    </span>
                  </div>
                  
                  {/* Show last successful analysis date if current analysis failed but we have old data */}
                  {provider.analysis.m3u_status !== 'valid' && provider.analysis.last_successful_analysis && (
                    <div className="flex items-center justify-between mb-2 p-1.5 bg-green-500/5 rounded border border-green-500/20">
                      <p className="text-[10px] text-green-400 flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Données sauvegardées du
                      </p>
                      <span className="text-[10px] text-green-400 font-medium">
                        {new Date(provider.analysis.last_successful_analysis).toLocaleString('fr-FR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  
                  {/* Status badges */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      provider.analysis.panel_status === 'online' 
                        ? 'bg-green-500/10 text-green-400' 
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {provider.analysis.panel_status === 'online' ? <Wifi className="h-2.5 w-2.5" /> : <WifiOff className="h-2.5 w-2.5" />}
                      Panel
                    </span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      provider.analysis.m3u_status === 'valid' 
                        ? 'bg-green-500/10 text-green-400' 
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {provider.analysis.m3u_status === 'valid' ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}
                      M3U
                    </span>
                    {provider.analysis.account_status && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        provider.analysis.account_status === 'Active' 
                          ? 'bg-blue-500/10 text-blue-400' 
                          : 'bg-yellow-500/10 text-yellow-400'
                      }`}>
                        {provider.analysis.account_status}
                      </span>
                    )}
                    {provider.analysis.account_expiration && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        new Date(provider.analysis.account_expiration) < new Date()
                          ? 'bg-red-500/10 text-red-400'
                          : new Date(provider.analysis.account_expiration) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                            ? 'bg-yellow-500/10 text-yellow-400'
                            : 'bg-emerald-500/10 text-emerald-400'
                      }`}>
                        <Calendar className="h-2.5 w-2.5" />
                        {new Date(provider.analysis.account_expiration).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  
                  {/* Content stats */}
                  {(provider.analysis.live_count > 0 || provider.analysis.vod_count > 0 || provider.analysis.series_count > 0) && (
                    <div className="grid grid-cols-3 gap-1 text-center">
                      <div className="bg-purple-500/10 rounded p-1">
                        <p className="text-xs font-bold text-purple-400">{(provider.analysis.live_count || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-muted-foreground">Live</p>
                      </div>
                      <div className="bg-blue-500/10 rounded p-1">
                        <p className="text-xs font-bold text-blue-400">{(provider.analysis.vod_count || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-muted-foreground">VOD</p>
                      </div>
                      <div className="bg-orange-500/10 rounded p-1">
                        <p className="text-xs font-bold text-orange-400">{(provider.analysis.series_count || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-muted-foreground">Séries</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-border">
                {/* Analyze Button - show only if panel_url or m3u_url is set */}
                {(provider.panel_url || provider.m3u_url) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAnalyzeProvider(provider)}
                    className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    data-testid={`analyze-provider-${provider.id}`}
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Analyser
                  </Button>
                )}
                {provider.analysis && (provider.analysis.live_count > 0 || provider.analysis.vod_count > 0 || provider.analysis.series_count > 0) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setStatsProvider(provider); setStatsDialogOpen(true); }}
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    data-testid={`view-stats-${provider.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Stats
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openHostsDialog(provider)}
                  data-testid={`manage-hosts-${provider.id}`}
                >
                  <Globe className="h-4 w-4 mr-1" />
                  Gérer Hosts
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(provider)}
                  data-testid={`edit-provider-${provider.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteProvider(provider.id)}
                  className="text-destructive hover:bg-destructive/10"
                  data-testid={`delete-provider-${provider.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* No results message */}
      {filteredProviders.length === 0 && providers.length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Aucun résultat</h3>
            <p className="text-muted-foreground mb-4">
              Aucun fournisseur ne correspond à vos critères de recherche.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchFilter("");
                setStatusFilter("all");
              }}
            >
              Réinitialiser les filtres
            </Button>
          </CardContent>
        </Card>
      )}

      {providers.length === 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Aucun fournisseur</h3>
            <p className="text-muted-foreground">
              Ajoutez des fournisseurs pour l'outil "Trouver votre host".
            </p>
          </CardContent>
        </Card>
      )}

      {/* Hosts Management Dialog */}
      <Dialog open={hostsDialogOpen} onOpenChange={setHostsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="hosts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Hosts de {selectedProvider?.name}
            </DialogTitle>
          </DialogHeader>
          
          {/* Actions bar */}
          <div className="flex flex-wrap items-center gap-2 py-3 border-b border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddHostDialogOpen(true)}
              data-testid="add-host-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter un host
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleScanHosts}
              disabled={scanningHosts || hosts.length === 0}
              data-testid="scan-hosts-btn"
            >
              {scanningHosts ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Scan en cours...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-1" />
                  Scanner les hosts
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkAddDialogOpen(true)}
              data-testid="bulk-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajout en masse
            </Button>
            {selectedHosts.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Supprimer ({selectedHosts.size})
              </Button>
            )}
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleValidateAllHosts}
              disabled={validatingHosts || hosts.length === 0 || !selectedProvider?.m3u_url}
              title={!selectedProvider?.m3u_url ? "URL M3U requise" : "Valider les hosts avec l'API M3U"}
              data-testid="validate-hosts-btn"
            >
              {validatingHosts ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Validation...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Valider Hosts
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLookupIPs}
              disabled={loadingIPs || hosts.length === 0}
              data-testid="lookup-ips-btn"
            >
              {loadingIPs ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Résolution...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-1" />
                  Résoudre IPs
                </>
              )}
            </Button>
          </div>

          {/* Hosts table */}
          <div className="flex-1 overflow-auto">
            {loadingHosts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
              </div>
            ) : hosts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun host</p>
              </div>
            ) : (
              <table className="w-full data-table">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="w-10 py-3 px-4">
                      <Checkbox
                        checked={selectedHosts.size === hosts.length && hosts.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-hosts"
                      />
                    </th>
                    <th className="text-left py-3 px-4">Host</th>
                    <th className="text-left py-3 px-4 w-20">Validité</th>
                    <th className="text-left py-3 px-4">IP</th>
                    <th className="text-left py-3 px-4">Note</th>
                    <th className="text-right py-3 px-4 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hosts.map((host) => (
                    <tr key={host.id} className="border-t border-border hover:bg-muted/20">
                      <td className="py-3 px-4">
                        <Checkbox
                          checked={selectedHosts.has(host.id)}
                          onCheckedChange={() => toggleSelectHost(host.id)}
                          data-testid={`select-host-${host.id}`}
                        />
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-accent">
                        {host.host}
                      </td>
                      <td className="py-3 px-4">
                        {host.is_valid === true ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 text-[10px] px-1.5 py-0">
                                OK
                              </Badge>
                              {host.response_time && (
                                <span className="text-[10px] text-green-400 font-mono">{host.response_time}ms</span>
                              )}
                            </div>
                            {host.last_check && (
                              <span className="text-[9px] text-muted-foreground mt-0.5">
                                ✓ {new Date(host.last_check).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        ) : host.is_valid === false ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-[10px] px-1.5 py-0">
                                ÉCHEC
                              </Badge>
                            </div>
                            {host.validation_error && (
                              <span className="text-[9px] text-red-400/70 mt-0.5 truncate max-w-[120px]" title={host.validation_error}>
                                {host.validation_error}
                              </span>
                            )}
                            {host.last_check && (
                              <span className="text-[9px] text-muted-foreground">
                                {new Date(host.last_check).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Non testé</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {hostIPs[host.host] ? (
                          hostIPs[host.host].status === 'success' ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-green-400">{hostIPs[host.host].ip}</span>
                              {hostIPs[host.host].country_code && (
                                <span className="text-xs text-muted-foreground">
                                  {getFlag(hostIPs[host.host].country_code)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-red-400">Erreur</span>
                          )
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => handleLookupSingleIP(host)}
                          >
                            <Search className="h-3 w-3 mr-1" />
                            Résoudre
                          </Button>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingHostId === host.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingNote}
                              onChange={(e) => setEditingNote(e.target.value)}
                              placeholder="Note..."
                              className="h-8 text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleUpdateNote(host.id);
                                if (e.key === 'Escape') setEditingHostId(null);
                              }}
                              data-testid={`edit-note-input-${host.id}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateNote(host.id)}
                            >
                              <Check className="h-4 w-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingHostId(null)}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className="text-sm text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-2"
                            onClick={() => startEditNote(host)}
                          >
                            {host.note || <span className="italic">Cliquer pour ajouter une note</span>}
                            <Pencil className="h-3 w-3 opacity-50" />
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditNote(host)}
                            data-testid={`edit-host-${host.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteHost(host.id)}
                            className="text-destructive hover:bg-destructive/10"
                            data-testid={`delete-host-${host.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Single Host Dialog */}
      <Dialog open={addHostDialogOpen} onOpenChange={setAddHostDialogOpen}>
        <DialogContent data-testid="add-host-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un host</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Host *</Label>
              <Input
                value={newHostData.host}
                onChange={(e) => setNewHostData({ ...newHostData, host: e.target.value })}
                placeholder="ex: stream.provider.com"
                className="font-mono"
                data-testid="new-host-input"
              />
              <p className="text-xs text-muted-foreground">
                Format: domaine.com ou sous-domaine.domaine.com:port
              </p>
            </div>
            <div className="space-y-2">
              <Label>Note (optionnel)</Label>
              <Input
                value={newHostData.note}
                onChange={(e) => setNewHostData({ ...newHostData, note: e.target.value })}
                placeholder="ex: Serveur principal"
                data-testid="new-host-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddHostDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddHost} data-testid="confirm-add-host">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Hosts Dialog */}
      <Dialog open={bulkAddDialogOpen} onOpenChange={(open) => {
        setBulkAddDialogOpen(open);
        if (!open) {
          setBulkHostsText("");
          setBulkErrors([]);
        }
      }}>
        <DialogContent className="max-w-xl" data-testid="bulk-add-dialog">
          <DialogHeader>
            <DialogTitle>Ajout en masse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Hosts (un par ligne ou séparés par virgule)</Label>
              <Textarea
                value={bulkHostsText}
                onChange={(e) => setBulkHostsText(e.target.value)}
                placeholder="stream1.provider.com&#10;stream2.provider.com&#10;cdn.provider.net"
                className="font-mono text-sm h-40"
                data-testid="bulk-hosts-textarea"
              />
            </div>
            
            {/* Errors display */}
            {bulkErrors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-3 max-h-32 overflow-y-auto">
                <p className="text-sm font-medium text-destructive mb-2">
                  Erreurs ({bulkErrors.length})
                </p>
                {bulkErrors.map((err, index) => (
                  <div key={index} className="text-xs text-destructive/80 flex items-start gap-2 mb-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span><strong>{err.host}</strong>: {err.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAddDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleBulkAdd} data-testid="confirm-bulk-add">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Analysis Dialog */}
      <Dialog open={analysisDialogOpen} onOpenChange={(open) => {
        setAnalysisDialogOpen(open);
        if (!open) {
          setAnalysisResult(null);
          setAnalysisProvider(null);
          setCategorySearchFilter("");
        }
      }}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col" data-testid="analysis-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-cyan-400" />
              Analyse de {analysisProvider?.name}
            </DialogTitle>
          </DialogHeader>
          
          {analyzing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 text-cyan-400 animate-spin mb-4" />
              <p className="text-muted-foreground">Analyse en cours...</p>
              <p className="text-xs text-muted-foreground mt-2">
                Vérification du panel et récupération des statistiques M3U
              </p>
            </div>
          ) : analysisResult?.error && !analysisResult?.panel_status && !analysisResult?.m3u_status ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <p className="text-destructive font-medium">Erreur d'analyse</p>
              <p className="text-sm text-muted-foreground mt-2">{analysisResult.error}</p>
            </div>
          ) : analysisResult && (
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="space-y-4 py-4">
                {/* Panel & M3U Status - Compact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Panel Status */}
                  <div className="bg-muted/30 rounded-lg p-3">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Server className="h-4 w-4 text-primary" />
                      Panel
                    </h3>
                    {analysisProvider?.panel_url ? (
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-sm ${
                          analysisResult.panel_status === 'online' 
                            ? 'bg-green-500/10 text-green-400' 
                            : analysisResult.panel_status === 'offline' 
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {analysisResult.panel_status === 'online' ? (
                            <Wifi className="h-3 w-3" />
                          ) : (
                            <WifiOff className="h-3 w-3" />
                          )}
                          <span className="font-medium">
                            {analysisResult.panel_status === 'online' ? 'En ligne' : 
                             analysisResult.panel_status === 'offline' ? 'Hors ligne' : 'Inconnu'}
                          </span>
                        </div>
                        {analysisResult.panel_response_time && (
                          <span className="text-xs text-muted-foreground">
                            {analysisResult.panel_response_time}ms
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Non configuré</p>
                    )}
                  </div>

                  {/* M3U Status */}
                  <div className="bg-muted/30 rounded-lg p-3">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Tv className="h-4 w-4 text-primary" />
                      Compte M3U
                    </h3>
                    {analysisProvider?.m3u_url ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-sm ${
                          analysisResult.m3u_status === 'valid' 
                            ? 'bg-green-500/10 text-green-400' 
                            : analysisResult.m3u_status === 'proxy_required'
                            ? 'bg-amber-500/10 text-amber-400'
                            : analysisResult.m3u_status === 'invalid' 
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {analysisResult.m3u_status === 'valid' ? (
                            <Check className="h-3 w-3" />
                          ) : analysisResult.m3u_status === 'proxy_required' ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                          <span className="font-medium">
                            {analysisResult.m3u_status === 'valid' ? 'Valide' : 
                             analysisResult.m3u_status === 'proxy_required' ? 'Blocage IP' :
                             analysisResult.m3u_status === 'invalid' ? 'Invalide' : '?'}
                          </span>
                        </div>
                        {analysisResult.account_status && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            analysisResult.account_status === 'Active' 
                              ? 'bg-green-500/10 text-green-400'
                              : 'bg-red-500/10 text-red-400'
                          }`}>
                            {analysisResult.account_status}
                          </span>
                        )}
                        {analysisResult.account_expiration && (
                          <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                            new Date(analysisResult.account_expiration) < new Date()
                              ? 'bg-red-500/10 text-red-400'
                              : new Date(analysisResult.account_expiration) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                                ? 'bg-yellow-500/10 text-yellow-400'
                                : 'bg-emerald-500/10 text-emerald-400'
                          }`}>
                            <Calendar className="h-2.5 w-2.5" />
                            {new Date(analysisResult.account_expiration).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Non configuré</p>
                    )}
                  </div>
                </div>

                {/* Error message if any */}
                {analysisResult.error && analysisResult.m3u_status === 'invalid' && (
                  <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-xs text-red-400">{analysisResult.error}</p>
                  </div>
                )}

                {/* Proxy mode - Manual test URLs */}
                {analysisResult.proxy_required && analysisResult.manual_test_urls && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-400 font-medium">Blocage IP Détecté</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {analysisResult.error}
                        </p>
                      </div>
                    </div>
                    <div className="border-t border-amber-500/20 pt-3">
                      <p className="text-xs text-muted-foreground mb-2">
                        Pour tester manuellement, ouvrez ce lien dans votre navigateur :
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-background/50 px-2 py-1.5 rounded border border-border overflow-x-auto">
                          {analysisResult.manual_test_urls.auth}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(analysisResult.manual_test_urls.auth);
                            toast.success("URL copiée !");
                          }}
                          className="flex-shrink-0"
                        >
                          <Link2 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const url = analysisResult.manual_test_urls.auth;
                            if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          className="flex-shrink-0"
                        >
                          <Globe className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {/* JSON Paste Section */}
                      <div className="mt-3 p-3 bg-background/50 rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-2">
                          <strong>Étapes :</strong> 1. Cliquez sur 🌐 pour ouvrir l'URL → 2. Copiez tout le JSON → 3. Collez ci-dessous
                        </p>
                        <Textarea
                          placeholder='Collez le JSON ici (ex: {"user_info":{"username":"..."},...})'
                          className="min-h-[80px] text-xs font-mono"
                          id="proxy-json-input"
                          onChange={async (e) => {
                            const value = e.target.value.trim();
                            if (value && value.startsWith('{')) {
                              try {
                                const authData = JSON.parse(value);
                                if (authData.user_info) {
                                  toast.info("JSON détecté ! Analyse en cours...");
                                  
                                  // Send to backend for processing
                                  const proxyData = {
                                    provider_id: analysisProvider.id,
                                    auth_data: authData,
                                    live_categories: null,
                                    live_streams: null,
                                    vod_categories: null,
                                    vod_streams: null,
                                    series_categories: null,
                                    series: null,
                                    error: null
                                  };
                                  
                                  const proxyResponse = await axios.post(
                                    `${API}/admin/providers/${analysisProvider.id}/analyze-proxy`,
                                    proxyData,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  
                                  setAnalysisResult(proxyResponse.data);
                                  fetchProviders();
                                  toast.success("Analyse terminée !");
                                  e.target.value = '';
                                }
                              } catch (err) {
                                // Not valid JSON yet, user still typing
                              }
                            }
                          }}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          L'analyse démarre automatiquement quand un JSON valide est détecté
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Statistics Section */}
                {analysisResult.stats && (
                  <div className="bg-muted/30 rounded-lg p-3">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-primary" />
                      Statistiques du contenu
                    </h3>
                    
                    {/* Stats Overview Cards - Compact */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-2 text-center">
                        <PlayCircle className="h-5 w-5 text-purple-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-purple-400">
                          {(analysisResult.stats.live_streams || 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase">Live</p>
                        <p className="text-[10px] text-purple-400/70">
                          {analysisResult.stats.live_categories || 0} cat.
                        </p>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 text-center">
                        <Film className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-blue-400">
                          {(analysisResult.stats.vod_streams || 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase">VOD</p>
                        <p className="text-[10px] text-blue-400/70">
                          {analysisResult.stats.vod_categories || 0} cat.
                        </p>
                      </div>
                      
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-2 text-center">
                        <Tv className="h-5 w-5 text-orange-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-orange-400">
                          {(analysisResult.stats.series || 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase">Séries</p>
                        <p className="text-[10px] text-orange-400/70">
                          {analysisResult.stats.series_categories || 0} cat.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Categories Details */}
                {analysisResult.categories && (
                  <div className="bg-muted/30 rounded-lg p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-primary" />
                      Détail des catégories
                    </h3>
                    
                    {/* Search filter */}
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Rechercher une catégorie..."
                          value={categorySearchFilter}
                          onChange={(e) => setCategorySearchFilter(e.target.value)}
                          className="pl-9 bg-background/50"
                        />
                        {categorySearchFilter && (
                          <button
                            onClick={() => setCategorySearchFilter("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <Tabs defaultValue="live" className="w-full" onValueChange={() => setCategorySearchFilter("")}>
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="live" className="flex items-center gap-2">
                          <PlayCircle className="h-3 w-3" />
                          Live ({analysisResult.categories.live?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="vod" className="flex items-center gap-2">
                          <Film className="h-3 w-3" />
                          VOD ({analysisResult.categories.vod?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="series" className="flex items-center gap-2">
                          <Tv className="h-3 w-3" />
                          Séries ({analysisResult.categories.series?.length || 0})
                        </TabsTrigger>
                      </TabsList>
                      
                      {['live', 'vod', 'series'].map((type) => {
                        // Sort categories by count (descending) and filter by search
                        const sortedCategories = (analysisResult.categories[type] || [])
                          .filter(cat => cat.name.toLowerCase().includes(categorySearchFilter.toLowerCase()))
                          .sort((a, b) => b.count - a.count);
                        
                        // Colors for each type
                        const colors = {
                          live: { bg: 'bg-purple-500', text: 'text-purple-400' },
                          vod: { bg: 'bg-blue-500', text: 'text-blue-400' },
                          series: { bg: 'bg-orange-500', text: 'text-orange-400' }
                        };
                        
                        return (
                          <TabsContent key={type} value={type} className="mt-4">
                            {/* Full list */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                                  {categorySearchFilter 
                                    ? `${sortedCategories.length} résultat(s)` 
                                    : `Toutes les catégories (${sortedCategories.length})`
                                  }
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Triées par nombre ↓
                                </p>
                              </div>
                              <ScrollArea className="h-[250px] border rounded-md">
                                <div className="p-2 space-y-1">
                                  {sortedCategories.length > 0 ? (
                                    sortedCategories.map((cat, idx) => (
                                      <div 
                                        key={idx}
                                        className="flex items-center justify-between p-2 rounded hover:bg-muted/50 text-sm group"
                                      >
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <span className="text-xs text-muted-foreground w-6">#{idx + 1}</span>
                                          <span className="text-foreground truncate" title={cat.name}>
                                            {cat.name}
                                          </span>
                                        </div>
                                        <Badge 
                                          variant="secondary" 
                                          className={`font-mono ml-2 ${colors[type].text} bg-${type === 'live' ? 'purple' : type === 'vod' ? 'blue' : 'orange'}-500/10`}
                                        >
                                          {cat.count.toLocaleString()}
                                        </Badge>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-sm text-muted-foreground text-center py-8">
                                      {categorySearchFilter 
                                        ? `Aucune catégorie ne correspond à "${categorySearchFilter}"`
                                        : "Aucune catégorie disponible"
                                      }
                                    </p>
                                  )}
                                </div>
                              </ScrollArea>
                            </div>
                          </TabsContent>
                        );
                      })}
                    </Tabs>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-auto">
            <Button variant="outline" onClick={() => setAnalysisDialogOpen(false)}>
              Fermer
            </Button>
            {!analyzing && (analysisProvider?.panel_url || analysisProvider?.m3u_url) && (
              <Button 
                onClick={() => handleAnalyzeProvider(analysisProvider)}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Relancer l'analyse
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats View Dialog - Vue rapide des statistiques sauvegardées */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="stats-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-green-400" />
              Statistiques de {statsProvider?.name}
            </DialogTitle>
          </DialogHeader>
          
          {statsProvider?.analysis ? (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-4">
                <Badge variant={statsProvider.analysis.m3u_status === 'valid' ? 'default' : 'destructive'}>
                  {statsProvider.analysis.m3u_status === 'valid' ? '✓ M3U Valide' : statsProvider.analysis.m3u_status}
                </Badge>
                {statsProvider.analysis.account_status && (
                  <Badge variant={statsProvider.analysis.account_status === 'Active' ? 'default' : 'secondary'}>
                    {statsProvider.analysis.account_status}
                  </Badge>
                )}
                {statsProvider.analysis.account_expiration && (
                  <span className="text-sm text-muted-foreground">
                    Expire: {statsProvider.analysis.account_expiration}
                  </span>
                )}
              </div>

              {/* Stats Grid avec nombre de catégories */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Statistiques du contenu
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                    <CardContent className="p-4 text-center">
                      <Tv className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-blue-400">
                        {(statsProvider.analysis.live_count || 0).toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">LIVE</p>
                      <p className="text-xs text-blue-400/70">
                        {statsProvider.analysis.live_categories_count || statsProvider.analysis.categories?.live?.length || 0} cat.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
                    <CardContent className="p-4 text-center">
                      <Film className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-purple-400">
                        {(statsProvider.analysis.vod_count || 0).toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">VOD</p>
                      <p className="text-xs text-purple-400/70">
                        {statsProvider.analysis.vod_categories_count || statsProvider.analysis.categories?.vod?.length || 0} cat.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
                    <CardContent className="p-4 text-center">
                      <PlayCircle className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-orange-400">
                        {(statsProvider.analysis.series_count || 0).toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">SÉRIES</p>
                      <p className="text-xs text-orange-400/70">
                        {statsProvider.analysis.series_categories_count || statsProvider.analysis.categories?.series?.length || 0} cat.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Détail des catégories */}
              {statsProvider.analysis.categories && (
                <StatsCategories categories={statsProvider.analysis.categories} />
              )}

              {/* Last Analysis Info */}
              {statsProvider.analysis.last_successful_analysis && (
                <p className="text-xs text-muted-foreground text-center">
                  Dernière analyse réussie: {new Date(statsProvider.analysis.last_successful_analysis).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Aucune statistique disponible. Lancez une analyse d'abord.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatsDialogOpen(false)}>
              Fermer
            </Button>
            <Button 
              onClick={() => {
                setStatsDialogOpen(false);
                handleAnalyzeProvider(statsProvider);
              }}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Relancer l'analyse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Dashboard Component
